"use client"

import { useState, useEffect, useCallback } from "react"
import Table from "../modules/table/table"
import Button from "../modules/Button/Button"
import Modal from "../modules/Modal/Moadal"
import styles from "../../../pages/inventory/InventoryDashboardPage.module.css"
import InfoCard from "../modules/InfoCard/InfoCard"
import StatusSelector from "../detail/StatusSelector"
import OrderPdfExporter from "./OrderPdfExporter"
import { getClientBulkOrders, updateClientBulkOrderStatus, fetchByUrl } from "../../../services/inventory/detail"
import { parseDateSafe } from "../../../utils/dateFormatters"

const PendingOrdersSection = ({ title = "Pending Orders", columns = [], defaultStatus = "pending" }) => {
  const [ordersData, setOrdersData] = useState({ results: [], meta: {} })
  const [loading, setLoading] = useState(false)
  const [paginationLoading, setPaginationLoading] = useState(false)

  const [orderDetailModal, setOrderDetailModal] = useState({ isOpen: false, selectedOrder: null })
  const [statusColorMap, setStatusColorMap] = useState({})
  const [statusOptions, setStatusOptions] = useState([])
  const [apiLoading, setApiLoading] = useState(false)

  const HARDCODED_STATUSES = ["pending", "processing", "shipped", "delivered", "cancelled"]
  const defaultStatusColors = {
    pending: { color: "#fef3c7", text: "#d97706", dotColor: "#d97706" },
    processing: { color: "#e0f2fe", text: "#0284c7", dotColor: "#0284c7" },
    shipped: { color: "#ddd6fe", text: "#7c3aed", dotColor: "#7c3aed" },
    delivered: { color: "#dcfce7", text: "#16a34a", dotColor: "#16a34a" },
    cancelled: { color: "#fee2e2", text: "#dc2626", dotColor: "#dc2626" },
  }

  useEffect(() => {
    const mappedColors = {}
    const options = HARDCODED_STATUSES.map((status) => {
      mappedColors[status] = defaultStatusColors[status] || {
        color: "#f3f4f6",
        text: "#374151",
        dotColor: "#6b7280",
      }
      return { label: status, value: status, ...mappedColors[status] }
    })
    setStatusColorMap(mappedColors)
    setStatusOptions(options)
  }, [])

  const mapOrders = (raw = []) =>
    raw.map((order) => ({
      id: order.client_bulkorderid || order.id,
      orderNumber: order.order_number,
      location: order.location_name,
      locationId: order.location_id,
      orderDate: order.ordered_at
        ? parseDateSafe(order.ordered_at)?.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) || ""
        : "",
      status: order.status,
      devices: order.items?.reduce((acc, item) => acc + (item.quantity || 0), 0) || 0,
      deviceType:
        order.items?.length === 1 ? order.items[0].device_type_name : order.items?.length > 1 ? "Mixed" : "N/A",
      orderedBy: order.ordered_by_name,
      tenantName: order.tenant_name,
      canceledAt: order.canceled_at,
      deliveredAt: order.delivered_at,
      tenant: order.tenant,
      cancelledBy: order.cancelled_by,
      originalOrder: order,
    }))

  const buildQueryParams = useCallback(
    (overrides = {}) => {
      const params = { ...overrides }
      if (defaultStatus) params.status = defaultStatus
      return params
    },
    [defaultStatus],
  )

  const fetchOrders = useCallback(
    async (urlOrParams = {}) => {
      setPaginationLoading(true)
      setLoading((s) => (s || typeof urlOrParams === "object" ? s : s))
      try {
        let response
        if (typeof urlOrParams === "string") {
          response = await fetchByUrl(urlOrParams)
        } else {
          const params = buildQueryParams(urlOrParams)
          response = await getClientBulkOrders(params)
        }

        if (response?.success) {
          const raw = response.data?.results ?? []
          const meta = response.data?.meta ?? {}
          const transformed = mapOrders(raw)
          setOrdersData({ results: transformed, meta })
        } else if (Array.isArray(response?.results)) {
          const transformed = mapOrders(response.results)
          setOrdersData({ results: transformed, meta: response.meta || {} })
        } else {
          setOrdersData({ results: [], meta: {} })
        }
      } catch (err) {
        console.error("Error fetching client bulk orders:", err)
        setOrdersData({ results: [], meta: {} })
      } finally {
        setLoading(false)
        setPaginationLoading(false)
      }
    },
    [buildQueryParams],
  )

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  const handleRowClick = (order) => {
    setOrderDetailModal({ isOpen: true, selectedOrder: order.originalOrder })
  }

  const handleStatusChange = async (newStatus) => {
    const orderId = orderDetailModal.selectedOrder?.client_bulkorderid
    if (!orderId) return
    setApiLoading(true)
    try {
      const response = await updateClientBulkOrderStatus(orderId, newStatus)
      const newStatusFromServer = response?.status || newStatus
      setOrderDetailModal((prev) => ({
        ...prev,
        selectedOrder: { ...prev.selectedOrder, status: newStatusFromServer },
      }))
      setOrdersData((prev) => ({
        ...prev,
        results: prev.results.map((o) => (o.id === orderId ? { ...o, status: newStatusFromServer } : o)),
      }))
    } catch (err) {
      console.error("Error updating client bulk order status:", err)
    } finally {
      setApiLoading(false)
    }
  }

  const handlePageChange = async (pageUrlOrNumber) => {
    setPaginationLoading(true)
    try {
      if (typeof pageUrlOrNumber === "string") {
        await fetchOrders(pageUrlOrNumber)
      } else if (typeof pageUrlOrNumber === "number") {
        const meta = ordersData.meta || {}
        const base = meta.base_url || meta.base || meta.first || null
        if (base && typeof base === "string") {
          const url = `${base}${base.includes("?") ? "&" : "?"}page=${pageUrlOrNumber}`
          await fetchOrders(url)
        } else {
          await fetchOrders({ page: pageUrlOrNumber })
        }
      }
    } catch (err) {
      console.error("Error changing page:", err)
    } finally {
      setPaginationLoading(false)
    }
  }

  const handlePrintLabel = () => {
    try {
      if (orderDetailModal.selectedOrder) {
        OrderPdfExporter.generate(orderDetailModal.selectedOrder, statusColorMap, {
          type: "pending",
          fileName: `order-${orderDetailModal.selectedOrder.order_number || orderDetailModal.selectedOrder.id}.pdf`,
        })
      }
    } catch (err) {
      console.error("Error generating PDF:", err)
    }
  }

  const orderDetailModalFooter = (
    <div className={styles.modal_footer_buttons}>
      {/* <Button variant="secondary" size="small" onClick={handlePrintLabel} disabled={apiLoading} title="Download PDF">
        Print Label
      </Button> */}
      <Button variant="secondary" onClick={() => setOrderDetailModal({ isOpen: false, selectedOrder: null })}>
        Close
      </Button>
    </div>
  )

  const buildOrderInfoData = (order) => {
    const deviceList = order?.items?.length
      ? order.items.map((device, idx) => (
          <div key={idx}>
            {device.device_type_name} — Qty: {device.quantity}
          </div>
        ))
      : "N/A"

    return [
      { label: "Order ID", value: order?.order_number },
      { label: "Ordered By", value: order?.ordered_by_name || "N/A" },
      { label: "Tenant Name", value: order?.tenant_name || "N/A" },
      { label: "Location", value: order?.location_name || "N/A" },
      { label: "Location ID", value: order?.location_id || "N/A" },
      { label: "Devices", value: deviceList },
      {
        label: "Order Date",
        value: order?.ordered_at
          ? parseDateSafe(order.ordered_at)?.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) || "N/A"
          : "N/A",
      },
      {
        label: "Canceled At",
        value: order?.canceled_at
          ? parseDateSafe(order.canceled_at)?.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) || "N/A"
          : "N/A",
      },
      {
        label: "Delivered At",
        value: order?.delivered_at
          ? parseDateSafe(order.delivered_at)?.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            }) || "N/A"
          : "N/A",
      },
      { label: "Cancelled By", value: order?.cancelled_by || "N/A" },
      {
        label: "Status",
        value: (
          <StatusSelector
            currentStatus={order?.status}
            onStatusChange={handleStatusChange}
            statusColorMap={statusColorMap}
            options={statusOptions}
            loading={apiLoading}
          />
        ),
      },
    ]
  }

  return (
    <div className={styles.section}>
      <div className={styles.orders_card}>
        <Table
          columns={columns}
          data={{ results: ordersData.results, meta: ordersData.meta }}
          height={700}
          loading={loading}
          pagination={true}
          meta={ordersData.meta}
          paginationLoading={paginationLoading}
          onPageChange={handlePageChange}
          isRowClickable={true}
          onRowClick={handleRowClick}
        />
      </div>

      {orderDetailModal.isOpen && orderDetailModal.selectedOrder && (
        <Modal
          title={`Order - ${orderDetailModal.selectedOrder.order_number}`}
          isOpen={orderDetailModal.isOpen}
          onClose={() => setOrderDetailModal({ isOpen: false, selectedOrder: null })}
          width="800px"
          height="85vh"
          footer={orderDetailModalFooter}
        >
          <div className={styles.detail_modal_}>
            <InfoCard title="Order Information" data={buildOrderInfoData(orderDetailModal.selectedOrder)} />
          </div>
        </Modal>
      )}
    </div>
  )
}

export default PendingOrdersSection
